import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BadgeCheck, BarChart3, IdCard, ShieldX } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { fetchVerifications, type AdminVerification } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A9.01 — Verifikatsiya arizalari navbati. GET /admin/verification (backend
 * `663ef50`). Userlar ariza beradi (POST /verification — D-yo'li ulaydi).
 */
export function VerificationQueue() {
  const t = useT()
  const navigate = useNavigate()

  const rowActions = useMemo<RowAction<AdminVerification>[]>(
    () => [
      { id: 'card', label: t.users.viewCard, icon: <IdCard size={13} />, onSelect: (r) => navigate(`/verification/${r.id}`) },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminVerification>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'user_id',
        header: t.col.name,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.user_id}`} className="text-accent hover:underline">
            {r.username ? `@${r.username}` : `#${r.user_id}`}
          </Link>
        ),
      },
      {
        key: 'kind',
        header: t.verif.kind,
        type: 'enum',
        width: 120,
        options: [
          { value: 'identity', label: t.verif.kindIdentity, status: 'review' },
          { value: 'org', label: t.verif.kindOrg, status: 'review' },
          { value: 'notable', label: t.verif.kindNotable, status: 'review' },
        ],
      },
      { key: 'evidence', header: t.verif.evidence, type: 'longtext', minWidth: 200 },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 120,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'approved', label: t.verif.approved, status: 'active' },
          { value: 'rejected', label: t.verif.rejected, status: 'deleted' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchVerifications, [])

  return (
    <>
      <PageHeader
        title={t.verif.queue}
        screenId="A9.01"
        description={t.verif.queueNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/verification/rejected"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <ShieldX size={13} />
              {t.verif.rejectedTitle}
            </Link>
            <Link
              to="/verification/stats"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BarChart3 size={13} />
              {t.verif.statsTitle}
            </Link>
            <Link
              to="/verification/registry"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              <BadgeCheck size={13} />
              {t.verif.registry}
            </Link>
          </div>
        }
      />
      <DataTable<AdminVerification>
        screenId="A9.01"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <BadgeCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
